/**
 * Toast Notification Component
 *
 * Provides a simple toast notification system for showing success/error messages.
 * Used for optimistic update feedback and error notifications.
 *
 * Requirements: 1.5 (optimistic updates with rollback notification)
 */

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

// =============================================================================
// Types
// =============================================================================

export type ToastType = 'success' | 'error' | 'info' | 'warning';

/** Optional inline action (e.g. Retry) rendered inside a toast. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (
    message: string,
    type?: ToastType,
    duration?: number,
    action?: ToastAction
  ) => void;
  dismissToast: (id: string) => void;
}

/**
 * Default lifetime by type. Errors linger far longer than the old flat 4s —
 * a 4s error routinely vanished before the user finished reading it (#20).
 * Callers can still override, and pass duration 0 to persist until dismissed.
 */
function defaultDurationFor(type: ToastType): number {
  return type === 'error' ? 8000 : 4000;
}

// =============================================================================
// Context
// =============================================================================

const ToastContext = createContext<ToastContextValue | null>(null);

// =============================================================================
// Hook
// =============================================================================

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// =============================================================================
// Provider
// =============================================================================

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', duration?: number, action?: ToastAction) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const resolvedDuration = duration ?? defaultDurationFor(type);
      const toast: Toast = { id, message, type, duration: resolvedDuration, action };

      setToasts((prev) => [...prev, toast]);

      // Auto-dismiss after duration (0 = persist until dismissed)
      if (resolvedDuration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, resolvedDuration);
      }
    },
    []
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

// =============================================================================
// Toast Container
// =============================================================================

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) {
    return null;
  }

  // No aria-live on the container: each toast carries its own live-region role
  // (assertive for errors, polite otherwise) so screen readers announce each
  // one once with the right urgency, instead of the container and item both
  // claiming to be live regions (#20).
  return (
    <div className="fixed bottom-20 left-0 right-0 z-[100] flex flex-col items-center gap-2 px-4 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// =============================================================================
// Toast Item
// =============================================================================

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const [isExiting, setIsExiting] = useState(false);

  // Handle exit animation
  useEffect(() => {
    if (toast.duration && toast.duration > 0) {
      const exitTimer = setTimeout(() => {
        setIsExiting(true);
      }, toast.duration - 300); // Start exit animation 300ms before removal

      return () => clearTimeout(exitTimer);
    }
  }, [toast.duration]);

  const handleDismiss = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [onDismiss, toast.id]);

  const { bgColor, textColor, icon } = getToastStyles(toast.type);
  const isError = toast.type === 'error';

  return (
    <div
      className={`
        pointer-events-auto max-w-sm w-full rounded-lg shadow-lg border
        ${bgColor} ${textColor}
        transform transition-all duration-300 ease-out
        ${isExiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}
      `}
      // Errors interrupt (assertive); everything else is announced politely.
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <div className="flex items-center gap-3 p-4">
        <span className="flex-shrink-0">{icon}</span>
        <p className="flex-1 text-sm font-medium">{toast.message}</p>
        {toast.action && (
          <button
            onClick={() => {
              toast.action?.onClick();
              handleDismiss();
            }}
            className="flex-shrink-0 px-3 py-1 rounded font-semibold text-sm bg-white/15 hover:bg-white/25 transition-colors min-h-[36px]"
          >
            {toast.action.label}
          </button>
        )}
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
          aria-label="Dismiss notification"
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function getToastStyles(type: ToastType) {
  switch (type) {
    case 'success':
      return {
        bgColor: 'bg-green-500/90 border-green-400/50',
        textColor: 'text-white',
        icon: <CheckIcon className="w-5 h-5" />,
      };
    case 'error':
      return {
        bgColor: 'bg-red-500/90 border-red-400/50',
        textColor: 'text-white',
        icon: <ErrorIcon className="w-5 h-5" />,
      };
    case 'warning':
      return {
        bgColor: 'bg-yellow-500/90 border-yellow-400/50',
        textColor: 'text-white',
        icon: <WarningIcon className="w-5 h-5" />,
      };
    case 'info':
    default:
      return {
        bgColor: 'bg-bbx-neon-blue/90 border-bbx-neon-blue/50',
        textColor: 'text-white',
        icon: <InfoIcon className="w-5 h-5" />,
      };
  }
}

// =============================================================================
// Icons
// =============================================================================

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

export default ToastProvider;
